// src/pages/subscription/CheckoutSuccess.jsx — Post-Stripe checkout redirect
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { auth, db } from '../../firebase';
import { doc, onSnapshot } from 'firebase/firestore';

export default function CheckoutSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('verifying'); // verifying | success | error

  useEffect(() => {
    if (!auth.currentUser) {
      navigate('/login');
      return;
    }

    // Listen to the user's company document for subscription activation
    const unsubUser = onSnapshot(doc(db, 'users', auth.currentUser.uid), (userDoc) => {
      if (!userDoc.exists()) return;
      const companyId = userDoc.data()?.companyId;
      if (!companyId) return;

      const unsubCompany = onSnapshot(doc(db, 'companies', companyId), (companyDoc) => {
        if (!companyDoc.exists()) return;
        const data = companyDoc.data();

        // Webhook has processed — subscription is now active
        if (data.subscriptionId && (data.subscriptionStatus === 'active' || data.subscriptionStatus === 'trialing')) {
          setStatus('success');
          // Redirect to home after 3 seconds
          setTimeout(() => navigate('/home'), 3000);
        }
      });

      // Clean up company listener when user listener fires again
      return () => unsubCompany();
    });

    // Fallback: redirect after 15 seconds even if webhook hasn't fired yet
    const timeout = setTimeout(() => {
      if (status === 'verifying') {
        setStatus('success');
        setTimeout(() => navigate('/home'), 2000);
      }
    }, 15000);

    return () => {
      unsubUser();
      clearTimeout(timeout);
    };
  }, [navigate, sessionId, status]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 flex items-center justify-center px-4">
      <div className="max-w-md w-full rounded-3xl border border-white/10 bg-white/5 backdrop-blur-xl p-8 shadow-2xl text-center text-white">
        {status === 'verifying' && (
          <>
            <div className="flex justify-center mb-6">
              <svg className="animate-spin h-12 w-12 text-purple-400" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold mb-2">Processing your payment...</h1>
            <p className="text-white/60 text-sm">
              Please wait while we confirm your subscription. This usually takes a few seconds.
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 border border-emerald-400/30 flex items-center justify-center">
                <svg className="w-8 h-8 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <h1 className="text-2xl font-bold mb-2">Payment successful!</h1>
            <p className="text-white/60 text-sm mb-6">
              Your subscription is now active. Redirecting to your dashboard...
            </p>
            <div className="w-full bg-white/10 rounded-full h-1.5 overflow-hidden">
              <div className="bg-gradient-to-r from-emerald-400 to-emerald-500 h-full rounded-full animate-pulse" style={{ width: '100%' }} />
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 rounded-full bg-red-500/20 border border-red-400/30 flex items-center justify-center">
                <svg className="w-8 h-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            </div>
            <h1 className="text-2xl font-bold mb-2">Something went wrong</h1>
            <p className="text-white/60 text-sm mb-6">
              We couldn't verify your payment. Don't worry — if your payment went through, it will appear shortly.
            </p>
            <button
              onClick={() => navigate('/home')}
              className="px-6 py-3 rounded-xl font-semibold bg-gradient-to-r from-fuchsia-500 via-purple-500 to-blue-500 text-white hover:shadow-xl transition-all"
            >
              Go to Dashboard
            </button>
          </>
        )}
      </div>
    </div>
  );
}
